import { useEffect, useMemo, useState } from 'react';
import { formatPercent, formatRial } from '@kia/shared';
import { api, errMsg } from '../api/client';
import { Button, Card, Chip, Field, Input, NumberInput } from '../ui';

/**
 * «آزمون بها» — Cost/Price Sense-Check (E11). A NON-PERSISTENT sandbox: enter one motor's BOM + prices +
 * (optionally) its own pool/output and watch the REAL engine compute cost → suggested price → margin, every
 * number shown WITH its formula and where it came from. The trust tool for the owner: does the engine agree
 * with what you know? Nothing is saved. Backed by POST /api/cost/sense-check (reuses domain/absorption.ts).
 */

interface Line {
  group: string;
  unit: string;
  qty: number;
  price: number;
  isWinding: boolean;
}
type Verdict = 'ok' | 'override' | 'blocked';
interface Result {
  material: number;
  windingWeightKg: number;
  freightPct: number;
  freight: number;
  eqKg: number;
  conversionRate: number;
  rateSource: 'current' | 'custom';
  conversion: number;
  cogs: number;
  floor: number;
  suggestedPrice: number;
  salePrice: number | null;
  margin: number | null;
  verdict: Verdict;
  blocking: string[];
}

// Two example lines so the screen shows a full, sensible breakdown on first open (a winding wire + a part).
const INITIAL_LINES: Line[] = [
  { group: 'مس و سیم لاکی', unit: 'کیلو گرم', qty: 2, price: 500_000, isWinding: true },
  { group: 'قطعات', unit: 'عدد', qty: 1, price: 200_000, isWinding: false },
];

const VERDICT: Record<Verdict, { tone: 'success' | 'warn' | 'danger'; label: string }> = {
  ok: { tone: 'success', label: '✅ آزاد' },
  override: { tone: 'warn', label: '⚠ نیازمند تأیید مدیر' },
  blocked: { tone: 'danger', label: '⛔ غیرقابل ترخیص' },
};

/** One output row: label, value, and the formula/provenance that produced it — the whole point (legibility). */
function Row({
  label,
  value,
  note,
  strong,
}: {
  label: string;
  value: string;
  note: string;
  strong?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 12,
        padding: '7px 0',
        borderTop: '1px solid var(--line)',
      }}
    >
      <div>
        <div style={{ fontWeight: strong ? 700 : 500 }}>{label}</div>
        <div style={{ color: 'var(--ink-3)', fontSize: '.82em' }}>{note}</div>
      </div>
      <div
        style={{
          fontWeight: strong ? 800 : 600,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </div>
    </div>
  );
}

export function SenseCheckScreen() {
  const [lines, setLines] = useState<Line[]>(INITIAL_LINES);
  const [useCurrentRate, setUseCurrentRate] = useState(true); // trust tool → open on the live Assumption-Master rate
  const [pool, setPool] = useState(10_000_000);
  const [expectedUnits, setExpectedUnits] = useState(100);
  const [freightPctInput, setFreightPctInput] = useState(0); // whole percent
  const [floorPct, setFloorPct] = useState(35); // whole percent
  const [salePrice, setSalePrice] = useState(2_500_000);
  const [res, setRes] = useState<Result | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const patch = (i: number, p: Partial<Line>) =>
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...p } : l)));
  const addRow = () =>
    setLines((ls) => [...ls, { group: '', unit: '', qty: 1, price: 0, isWinding: false }]);
  const removeRow = (i: number) => setLines((ls) => ls.filter((_, j) => j !== i));

  // Serialize the inputs so the debounced effect fires on any change.
  const payload = useMemo(
    () => ({
      lines: lines.map((l) => ({
        group: l.group,
        unit: l.unit,
        qty: l.qty,
        price: l.price,
        isWinding: l.isWinding,
      })),
      useCurrentRate,
      pool: useCurrentRate ? null : pool,
      expectedUnits: useCurrentRate ? null : expectedUnits,
      freightPct: freightPctInput / 100,
      floor: floorPct / 100,
      salePrice: salePrice > 0 ? salePrice : null,
    }),
    [lines, useCurrentRate, pool, expectedUnits, freightPctInput, floorPct, salePrice],
  );

  useEffect(() => {
    const t = setTimeout(() => {
      void api<Result>('/api/cost/sense-check', {
        method: 'POST',
        body: JSON.stringify(payload),
      }).then((r) => {
        if (r.ok) {
          setRes(r.body as Result);
          setErr(null);
        } else {
          setRes(null);
          setErr(errMsg(r, 'خطا در محاسبه'));
        }
      });
    }, 300);
    return () => clearTimeout(t);
  }, [payload]);

  const fmt = (n: number) => formatRial(Math.round(n));
  const v = res ? VERDICT[res.verdict] : null;

  return (
    <div className="page">
      <h1>آزمون بها</h1>
      <p className="muted" style={{ marginTop: 0 }}>
        یک موتور را همین‌جا بسازید و ببینید موتور بهای تمام‌شده، قیمت پیشنهادی و حاشیه را چطور حساب
        می‌کند — با همان موتورِ واقعیِ محاسبه. <b>آزمایشی است و چیزی ذخیره نمی‌شود.</b>
      </p>

      <div className="row" style={{ gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* ── Inputs ── */}
        <div style={{ flex: '1 1 520px' }}>
          <Card title="اقلام موتور (BOM)">
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>گروه</th>
                    <th>واحد</th>
                    <th>مقدار</th>
                    <th>قیمت واحد</th>
                    <th>سیم‌پیچ</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={i}>
                      <td>
                        <Input
                          value={l.group}
                          onChange={(e) => patch(i, { group: e.target.value })}
                        />
                      </td>
                      <td style={{ width: 90 }}>
                        <Input
                          value={l.unit}
                          onChange={(e) => patch(i, { unit: e.target.value })}
                        />
                      </td>
                      <td style={{ width: 90 }}>
                        <NumberInput
                          value={l.qty}
                          onValue={(n) => patch(i, { qty: n })}
                          allowDecimal
                        />
                      </td>
                      <td style={{ width: 130 }}>
                        <NumberInput value={l.price} onValue={(n) => patch(i, { price: n })} />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={l.isWinding}
                          onChange={(e) => patch(i, { isWinding: e.target.checked })}
                          title="سیم لاکی — وزن آن مبنای جذب سربار است"
                        />
                      </td>
                      <td>
                        <button
                          onClick={() => removeRow(i)}
                          aria-label="حذف ردیف"
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--red)',
                            cursor: 'pointer',
                          }}
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button variant="secondary" onClick={addRow} style={{ marginTop: 8 }}>
              + ردیف
            </Button>

            <div className="row" style={{ gap: 12, flexWrap: 'wrap', marginTop: 16 }}>
              <Field label="نرخ جذب">
                <select
                  className="input"
                  value={useCurrentRate ? 'current' : 'custom'}
                  onChange={(e) => setUseCurrentRate(e.target.value === 'current')}
                >
                  <option value="custom">سفارشی (استخر و تعداد این موتور)</option>
                  <option value="current">از ماه جاری</option>
                </select>
              </Field>
              {!useCurrentRate ? (
                <>
                  <Field label="استخر تبدیل ماه (﷼)">
                    <div style={{ width: 160 }}>
                      <NumberInput value={pool} onValue={setPool} />
                    </div>
                  </Field>
                  <Field label="تعداد ماهانهٔ این موتور">
                    <div style={{ width: 120 }}>
                      <NumberInput value={expectedUnits} onValue={setExpectedUnits} />
                    </div>
                  </Field>
                </>
              ) : null}
              <Field label="حمل ورودی (٪)">
                <div style={{ width: 90 }}>
                  <NumberInput value={freightPctInput} onValue={setFreightPctInput} allowDecimal />
                </div>
              </Field>
              <Field label="کف سود (٪)">
                <div style={{ width: 90 }}>
                  <NumberInput value={floorPct} onValue={setFloorPct} allowDecimal />
                </div>
              </Field>
              <Field label="قیمت فروش هدف (﷼)">
                <div style={{ width: 160 }}>
                  <NumberInput value={salePrice} onValue={setSalePrice} />
                </div>
              </Field>
            </div>
          </Card>
        </div>

        {/* ── Breakdown ── */}
        <div style={{ flex: '1 1 360px' }}>
          <Card
            title={
              <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
                نتیجه
                {v ? <Chip tone={v.tone}>{v.label}</Chip> : null}
              </span>
            }
          >
            {err ? (
              <p style={{ color: 'var(--red)' }}>{err}</p>
            ) : !res ? (
              <p className="muted">در حال محاسبه…</p>
            ) : (
              <>
                <Row label="بهای مواد" value={fmt(res.material)} note="Σ مقدار × قیمت هر قلم" />
                <Row
                  label="حمل ورودی"
                  value={fmt(res.freight)}
                  note={`مواد × ${formatPercent(res.freightPct)}`}
                />
                <Row
                  label="وزن معادل (eqKg)"
                  value={res.eqKg.toLocaleString('fa-IR', { maximumFractionDigits: 2 })}
                  note={
                    res.windingWeightKg > 0
                      ? 'وزن سیم‌پیچ (کیلوگرم)'
                      : 'برآورد از بهای مواد (محصول بدون سیم‌پیچ)'
                  }
                />
                <Row
                  label="نرخ جذب سربار"
                  value={`${fmt(res.conversionRate)} /kg`}
                  note={
                    res.rateSource === 'current'
                      ? 'از پارامترهای ماه جاری'
                      : 'استخر ÷ (تعداد × وزن معادل)'
                  }
                />
                <Row
                  label="دستمزد و سربار جذب‌شده"
                  value={fmt(res.conversion)}
                  note="وزن معادل × نرخ جذب"
                />
                <Row
                  label="بهای تمام‌شده"
                  value={fmt(res.cogs)}
                  note="مواد + حمل + دستمزد و سربار"
                  strong
                />
                <Row
                  label="قیمت پیشنهادی"
                  value={fmt(res.suggestedPrice)}
                  note={`بها ÷ (۱ − کف ${formatPercent(res.floor)})`}
                  strong
                />
                {res.margin != null ? (
                  <Row
                    label="حاشیه در قیمت هدف"
                    value={formatPercent(res.margin)}
                    note="(قیمت فروش − بها) ÷ قیمت فروش"
                    strong
                  />
                ) : null}
                {res.blocking.length ? (
                  <p style={{ color: 'var(--amber, #b45309)', marginTop: 10, lineHeight: 1.9 }}>
                    {res.blocking.join(' • ')}
                  </p>
                ) : null}
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
